// deps
import React, { ReactElement, memo, useState, useEffect, useCallback, useContext, useMemo } from "react";

// internal deps
import { DeliveryInstruction, CartModal } from "@swiggy-private/cart-ui";
import { DeliveryInstructionDetail, DeliveryInstructionCroutonDetail } from "@swiggy-private/cart-types";
import { AnalyticsContext, isJioHotstarFlow } from "@swiggy-private/checkout-core";
import { useEffectOnce } from "@swiggy-private/core-ui";
import { ElementVisibilitySensor } from "@swiggy-private/dls-web-core/dist/components/ElementVisibilitySensor";

// constants
import {
    NO_ADDRESS_SELECTED_POPUP_DATA,
    DIRECTIONS_TO_REACH,
    FTUE_BENEFIT_TEXT_COUNT,
    BENEFIT_TEXT_KEY,
    VOICE_DIRECTION_TOOLTIP_COUNT,
} from "@cart-client/constants/CartDeliveryInstruction";

// helpers
import {
    prepareConstructInstructorText,
    prepareRequestPayload,
    checkForAddressValidity,
    openEditDirectionsToReach,
    getDirectionsToReachCardPosition,
    canShowVoiceDirectionTooltip,
    getVoiceDirectionTooltipCount,
    updateVoiceDirectionTooltipCount,
    getVoiceDirectionContext,
} from "@cart-client/helpers/CartDeliveryInstruction";
import { firstTimeUserExperienceHelper } from "@cart-client/utils/Core";

// analytics
import {
    impressionCartDeliveryInstruction,
    impressionNoAddressSelected,
    impressionDeliveryInstructionSubtext,
    clickDeliveryInstructionItem,
    impressionDirectionsToReachCard,
    clickDirectionsToReachCard,
    impressionVoiceDirectionTooltip,
    clickDeliveryInstructionCrouton,
    impressionDeliveryInstructionCrouton,
} from "@cart-client/analytics/events";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";

interface CartDeliveryInstructionProps {
    instructionArray: DeliveryInstructionDetail[];

    deliveryInstructionCroutonDetail?: DeliveryInstructionCroutonDetail;
    canShowCrouton: boolean;
    scrollToDIContainer: VoidFunction;
    setCroutonVisibility: (croutonVisibility: boolean) => void;
    onImpressionDeliveryInstructionGrouping: () => void;
    onImpressionDeliveryInstructionGroupingActual: () => void;
}

function CartDeliveryInstruction(props: CartDeliveryInstructionProps): ReactElement {
    const { onImpressionDeliveryInstructionGrouping, onImpressionDeliveryInstructionGroupingActual } = props;

    const [selectedInstructions, setSelectedInstructions] = useState<Record<string, DeliveryInstructionDetail>>({}); // api and instructor text
    const [instructorText, setInstructorText] = useState("");
    const [showAddressPopup, setShowAddressPopup] = useState(false);
    const [showBenefitText, setShowBenefitText] = useState(false);
    const [showVoiceDirectionTooltip, setShowVoiceDirectionTooltip] = useState(false);
    const [voiceDirectionTooltipCount, setVoiceDirectionTooltipCount] = useState(0);

    const { updateCartInProgress, doUpdateCart } = useContext(UpdateCartContext);

    const {
        cartState: { localCart, reviewedCart: cartResponse },
    } = useContext(CartContext);

    const { selectedAddress } = localCart || {};
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);

    useEffectOnce(() => {
        onImpressionDeliveryInstructionGrouping();
    });

    const onVisibilityChange = useCallback(
        (isVisible) => {
            if (isVisible) {
                onImpressionDeliveryInstructionGroupingActual?.();
            }
        },
        [onImpressionDeliveryInstructionGroupingActual],
    );

    const cartId = useMemo(() => `${cartResponse?.data?.cartId || ""}`, [cartResponse]);
    const addressId = useMemo(() => `${cartResponse?.data?.address_id || ""}`, [cartResponse]);

    const instructions = useMemo(() => {
        return [...props.instructionArray];
    }, [props.instructionArray]);

    const onDeliveryInstructionCroutonImpression = useCallback(() => {
        impressionDeliveryInstructionCrouton(`${cartResponse?.data?.cartId || ""}`, impressionEvent);
    }, [cartResponse, impressionEvent]);

    const onDeliveryInstructionCroutonClick = useCallback(() => {
        clickDeliveryInstructionCrouton(`${cartResponse?.data?.cartId || ""}`, clickEvent);
    }, [cartResponse, clickEvent]);

    const onDirectionsToReachSelection = useCallback((): boolean => {
        // if the address is not selected -> show popup
        const validAddress = checkForAddressValidity(selectedAddress);
        if (!validAddress) {
            impressionNoAddressSelected(impressionEvent);
            setShowAddressPopup(true);
        }
        return validAddress;
    }, [selectedAddress, impressionEvent]);

    const triggerUpdateCart = useCallback(
        async (dropOffInstructionIds: string[]) => {
            if (!updateCartInProgress)
                // eslint-disable-next-line @typescript-eslint/naming-convention
                await doUpdateCart({ ...localCart, selected_drop_off_instruction_ids: dropOffInstructionIds });
        },
        [updateCartInProgress, doUpdateCart, localCart],
    );

    const openEditDirectionsToReachHalfCard = useCallback(async () => {
        try {
            const nativeResponse = await openEditDirectionsToReach(selectedAddress);
            if (nativeResponse?.isDirectionsToReachUpdated) {
                const ids = prepareRequestPayload(selectedInstructions);
                triggerUpdateCart(ids);
            }
        } catch (_) {
            // no catch
        }
    }, [selectedAddress, triggerUpdateCart, selectedInstructions]);

    const onVoiceDirectionTooltipImpression = useCallback(
        (displayText: string) => {
            updateVoiceDirectionTooltipCount(voiceDirectionTooltipCount + 1);
            impressionVoiceDirectionTooltip(
                getVoiceDirectionContext({ cartId, addressId, displayText }),
                impressionEvent,
            );
        },
        [voiceDirectionTooltipCount, cartId, addressId, impressionEvent],
    );

    const directionsToReachCardImpression = useCallback(() => {
        const position = getDirectionsToReachCardPosition(instructions);
        if (position !== -1) {
            const contextValue = getVoiceDirectionContext({
                selectedAddress,
                position: position + 1,
                cartId,
                addressId,
            });
            impressionDirectionsToReachCard(contextValue, impressionEvent);
        }
    }, [instructions, selectedAddress, cartId, addressId, impressionEvent]);

    const directionsToReachCardClick = useCallback(
        (title: string) => {
            const position = getDirectionsToReachCardPosition(instructions);
            if (position !== -1) {
                const contextValue = getVoiceDirectionContext({
                    title,
                    selectedAddress,
                    position: position + 1,
                    cartId,
                    addressId,
                });
                clickDirectionsToReachCard(title, contextValue, clickEvent);
            }
        },
        [instructions, selectedAddress, cartId, addressId, clickEvent],
    );

    const onInstructionClick = useCallback(
        (item: DeliveryInstructionDetail) => {
            if (item.instruction_type !== DIRECTIONS_TO_REACH) {
                clickDeliveryInstructionItem(item.title, clickEvent);
            }

            item.selected = item.instruction_type !== DIRECTIONS_TO_REACH && !item.selected;

            if (item.instruction_type === DIRECTIONS_TO_REACH) {
                directionsToReachCardClick(item.title);
                if (!onDirectionsToReachSelection()) {
                    return;
                }
                // open directions to reach half card
                openEditDirectionsToReachHalfCard();
                return;
            }

            const newSelection = { ...selectedInstructions };

            if (item.selected) {
                newSelection[item.id] = item;
                if (item.incompatible_instruction_ids.length > 0) {
                    item.incompatible_instruction_ids.map((id: string) => {
                        if (newSelection[id]) {
                            delete newSelection[id];
                        }
                    });
                }
            } else {
                delete newSelection[item.id];
            }

            const ids = prepareRequestPayload(newSelection);
            setSelectedInstructions(newSelection);
            triggerUpdateCart(ids);
        },
        [
            selectedInstructions,
            onDirectionsToReachSelection,
            triggerUpdateCart,
            openEditDirectionsToReachHalfCard,
            directionsToReachCardClick,
            clickEvent,
        ],
    );

    const onCloseModal = useCallback(() => {
        setShowAddressPopup(false);
    }, [setShowAddressPopup]);

    // helper function to build the display text
    const getInstructorText = useCallback(() => {
        const resultStr = prepareConstructInstructorText(selectedInstructions);
        if (resultStr) impressionDeliveryInstructionSubtext(impressionEvent);

        setInstructorText(resultStr);
    }, [selectedInstructions, impressionEvent]);

    const toggleBenefitText = async (): Promise<void> => {
        const benefitTextFlag = await firstTimeUserExperienceHelper(BENEFIT_TEXT_KEY, FTUE_BENEFIT_TEXT_COUNT);
        setShowBenefitText(benefitTextFlag);
    };

    const shouldShowVoiceDirectionsTooltip = async (): Promise<void> => {
        if (canShowVoiceDirectionTooltip(selectedAddress) && !isJioHotstarFlow()) {
            const currentCount = await getVoiceDirectionTooltipCount();
            if (currentCount !== undefined) {
                setVoiceDirectionTooltipCount(currentCount);
                if (currentCount < VOICE_DIRECTION_TOOLTIP_COUNT) {
                    setShowVoiceDirectionTooltip(true);
                }
            }
        }
    };

    useEffect(() => {
        const selectedInstructions: Record<string, DeliveryInstructionDetail> = {};
        if (instructions.length) {
            impressionCartDeliveryInstruction(impressionEvent);
            directionsToReachCardImpression();
            instructions.map((item: DeliveryInstructionDetail) => {
                if (item.selected) {
                    selectedInstructions[item.id] = item;
                }
            });
            setSelectedInstructions(selectedInstructions);
        }
    }, [impressionEvent, directionsToReachCardImpression, instructions]);

    // whenever the user updates selection of drop-off instruction, update display text
    useEffect(() => {
        getInstructorText();
    }, [selectedInstructions, getInstructorText]);

    useEffectOnce(() => {
        toggleBenefitText();
        shouldShowVoiceDirectionsTooltip();
    });

    return (
        <ElementVisibilitySensor senseOnce onVisibilityChange={onVisibilityChange}>
            <DeliveryInstruction
                instructionArray={instructions}
                displayText={instructorText}
                onItemClick={onInstructionClick}
                showBenefitText={showBenefitText}
                deliveryInstructionCroutonDetail={props.deliveryInstructionCroutonDetail}
                canShowCrouton={props.canShowCrouton}
                scrollToDIContainer={props.scrollToDIContainer}
                analyticsEvents={{
                    onVoiceDirectionTooltipImpression: onVoiceDirectionTooltipImpression,
                    onDeliveryInstructionCroutonClick: onDeliveryInstructionCroutonClick,
                    onDeliveryInstructionCroutonImpression: onDeliveryInstructionCroutonImpression,
                }}
                showVoiceDirectionTooltip={showVoiceDirectionTooltip}
                setCroutonVisibility={props.setCroutonVisibility}
            />
            {showAddressPopup && <CartModal data={NO_ADDRESS_SELECTED_POPUP_DATA} closeModal={onCloseModal} />}
        </ElementVisibilitySensor>
    );
}

export default memo(CartDeliveryInstruction);
